<script lang="ts">
  /**
   * Härd-segment admin-CRUD-vy (S-04-20b).
   *
   * Härd-segment är sub-sträckor på en runda. Admin väljer först en
   * runda i dropdown, sen visas segment-listan för den rundan.
   *
   * Geometri-input i denna iteration: textarea med WKT MULTILINESTRING.
   * Kart-rit-flöde direkt på MapLibre kommer i S-04-17c — för pilot
   * räcker manuell WKT eftersom pilot-administratörer förväntas vara
   * tekniskt kunniga och rundorna är få.
   *
   * Hard-rules (CLAUDE.md):
   *  - Inga fritextfält (kategoriskt name + WKT är teknisk geometri).
   *  - Soft-delete via deactivate-endpoint, ingen hård-DELETE.
   *  - Inga råa databas-ID:n.
   *  - Audit-trail skrivs av backend (`route_hearth_*`-prefix).
   */
  import { onMount } from 'svelte';
  import {
    createSegment,
    deactivateSegment,
    isActive,
    listSegments,
    PRIORITIES,
    priorityLabel,
    reactivateSegment,
    updateSegment,
    type CreateSegmentInput,
    type SegmentPriority,
    type SegmentRecord,
    type UpdateSegmentInput,
  } from './segmentsClient';
  import { listHearths, type HearthRecord } from '../hearths/hearthsClient';

  type Props = {
    onUnauthorized?: () => void;
    /**
     * Signal-counter från RitaView (UX-fix 2026-05-05): `+` och Refresh
     * är lyfta till Rita-layer-toolbar-raden. Vid klick ökas respektive
     * counter, och vi lyssnar via $effect och kallar `startCreate` /
     * `loadSegments`. startCreate kräver vald runda — om ingen är vald
     * blir `+` en no-op (early return i startCreate).
     */
    createTrigger?: number;
    refreshTrigger?: number;
  };
  const { onUnauthorized, createTrigger = 0, refreshTrigger = 0 }: Props = $props();

  // lastSeen-räknare. Initialiseras vid första effect-körning så vi inte
  // auto-triggar create/refresh på initial-render. Måste sättas i $effect
  // för att Svelte 5 ska se prop-värdet (state_referenced_locally).
  let lastCreateTrigger: number | undefined = undefined;
  let lastRefreshTrigger: number | undefined = undefined;

  $effect(() => {
    if (lastCreateTrigger === undefined) {
      lastCreateTrigger = createTrigger;
      return;
    }
    if (createTrigger !== lastCreateTrigger) {
      lastCreateTrigger = createTrigger;
      startCreate();
    }
  });

  $effect(() => {
    if (lastRefreshTrigger === undefined) {
      lastRefreshTrigger = refreshTrigger;
      return;
    }
    if (refreshTrigger !== lastRefreshTrigger) {
      lastRefreshTrigger = refreshTrigger;
      void loadSegments();
    }
  });

  type Mode = 'list' | 'create' | 'edit';

  let mode: Mode = $state('list');
  let routes: HearthRecord[] = $state([]);
  let routesLoading = $state(true);
  let routesError: string | null = $state(null);

  let selectedRouteId: string = $state('');
  let segments: SegmentRecord[] = $state([]);
  let segmentsLoading = $state(false);
  let segmentsError: string | null = $state(null);

  // Form state
  let selectedSegment: SegmentRecord | null = $state(null);
  let formName: string = $state('');
  let formGeometry: string = $state('MULTILINESTRING((11.948 57.738, 11.949 57.739))');
  let formPriority: SegmentPriority = $state('medium');
  let formActiveFrom: string = $state('');
  let formActiveTo: string = $state('');
  let formError: string | null = $state(null);
  let isSaving = $state(false);

  let rowError: string | null = $state(null);
  let pendingActionId: string | null = $state(null);

  async function loadRoutes(): Promise<void> {
    routesLoading = true;
    routesError = null;
    const result = await listHearths();
    if (result.kind === 'ok') {
      routes = result.items;
      // Default-välj första aktiva rundan om ingen är vald.
      if (!selectedRouteId && routes.length > 0) {
        selectedRouteId = routes[0].id;
        await loadSegments();
      }
    } else if (result.kind === 'unauthorized') {
      routes = [];
      routesError = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else {
      routes = [];
      routesError = result.message;
    }
    routesLoading = false;
  }

  async function loadSegments(): Promise<void> {
    if (!selectedRouteId) {
      segments = [];
      return;
    }
    segmentsLoading = true;
    segmentsError = null;
    const result = await listSegments(selectedRouteId);
    if (result.kind === 'ok') {
      segments = result.items;
    } else if (result.kind === 'unauthorized') {
      segments = [];
      segmentsError = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else if (result.kind === 'route_not_found') {
      segments = [];
      segmentsError = 'Rundan finns inte längre.';
    } else {
      segments = [];
      segmentsError = result.kind === 'forbidden' ? 'Saknar behörighet.' : result.message;
    }
    segmentsLoading = false;
  }

  onMount(() => {
    void loadRoutes();
  });

  async function handleRouteChange(): Promise<void> {
    mode = 'list';
    selectedSegment = null;
    await loadSegments();
  }

  function startCreate(): void {
    if (!selectedRouteId) return;
    mode = 'create';
    selectedSegment = null;
    formName = '';
    formGeometry = 'MULTILINESTRING((11.948 57.738, 11.949 57.739))';
    formPriority = 'medium';
    formActiveFrom = '';
    formActiveTo = '';
    formError = null;
  }

  function startEdit(record: SegmentRecord): void {
    mode = 'edit';
    selectedSegment = record;
    formName = record.name;
    formGeometry = record.geometryWkt;
    formPriority = record.priority;
    formActiveFrom = isoToLocalInput(record.activeFrom);
    formActiveTo = record.activeTo ? isoToLocalInput(record.activeTo) : '';
    formError = null;
  }

  function cancelEditor(): void {
    mode = 'list';
    selectedSegment = null;
    formError = null;
  }

  function isoToLocalInput(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  function localInputToIso(value: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  function validateForm(): string | null {
    if (formName.trim().length === 0) return 'Ange ett namn för segmentet.';
    if (formName.length > 100) return 'Namn max 100 tecken.';
    if (!formGeometry.trim().toUpperCase().startsWith('MULTILINESTRING')) {
      return 'Geometrin måste börja med MULTILINESTRING.';
    }
    if (formActiveFrom && formActiveTo) {
      const from = new Date(formActiveFrom);
      const to = new Date(formActiveTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return 'Ogiltigt tidsfönster.';
      }
      if (to.getTime() <= from.getTime()) {
        return 'Slut måste vara efter start.';
      }
    }
    return null;
  }

  async function handleSubmit(): Promise<void> {
    if (!selectedRouteId) return;
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }
    isSaving = true;
    try {
      const activeFromIso = localInputToIso(formActiveFrom);
      const activeToIso = localInputToIso(formActiveTo);

      if (mode === 'create') {
        const input: CreateSegmentInput = {
          name: formName.trim(),
          geometryWkt: formGeometry.trim(),
          priority: formPriority,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
        };
        const result = await createSegment(selectedRouteId, input);
        if (result.kind === 'ok') {
          mode = 'list';
          await loadSegments();
        } else if (result.kind === 'unauthorized') {
          onUnauthorized?.();
        } else if (result.kind === 'invalid') {
          formError = result.message;
        } else if (result.kind === 'route_not_found') {
          formError = 'Rundan finns inte längre — välj en annan.';
        } else {
          formError =
            result.kind === 'forbidden'
              ? 'Saknar behörighet.'
              : result.kind === 'error'
                ? result.message
                : 'Något gick fel.';
        }
      } else if (mode === 'edit' && selectedSegment) {
        if (!activeFromIso) {
          formError = 'Ogiltigt start-datum.';
          return;
        }
        const input: UpdateSegmentInput = {
          name: formName.trim(),
          geometryWkt: formGeometry.trim(),
          priority: formPriority,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
        };
        const result = await updateSegment(selectedRouteId, selectedSegment.id, input);
        if (result.kind === 'ok') {
          mode = 'list';
          selectedSegment = null;
          await loadSegments();
        } else if (result.kind === 'unauthorized') {
          onUnauthorized?.();
        } else if (result.kind === 'invalid') {
          formError = result.message;
        } else if (result.kind === 'not_found') {
          formError = 'Segmentet finns inte längre.';
        } else {
          formError =
            result.kind === 'forbidden'
              ? 'Saknar behörighet.'
              : result.kind === 'error'
                ? result.message
                : 'Något gick fel.';
        }
      }
    } finally {
      isSaving = false;
    }
  }

  async function handleDeactivate(record: SegmentRecord): Promise<void> {
    if (!selectedRouteId) return;
    rowError = null;
    pendingActionId = record.id;
    try {
      const result = await deactivateSegment(selectedRouteId, record.id);
      if (result.kind === 'ok') {
        await loadSegments();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowError = 'Segmentet finns inte längre.';
        await loadSegments();
      } else {
        rowError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : (result as { message: string }).message;
      }
    } finally {
      pendingActionId = null;
    }
  }

  async function handleReactivate(record: SegmentRecord): Promise<void> {
    if (!selectedRouteId) return;
    rowError = null;
    pendingActionId = record.id;
    try {
      const result = await reactivateSegment(selectedRouteId, record.id);
      if (result.kind === 'ok') {
        await loadSegments();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowError = 'Segmentet finns inte längre.';
        await loadSegments();
      } else {
        rowError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : (result as { message: string }).message;
      }
    } finally {
      pendingActionId = null;
    }
  }
</script>

<section class="segments" aria-label="Härd-segment">
  {#if mode === 'list'}
    <div class="route-selector">
      <label class="field">
        <span>Välj runda</span>
        <select
          bind:value={selectedRouteId}
          onchange={() => void handleRouteChange()}
          disabled={routesLoading || routes.length === 0}
        >
          {#if routesLoading}
            <option value="">Hämtar rundor…</option>
          {:else if routes.length === 0}
            <option value="">Inga rundor finns — skapa en runda först</option>
          {:else}
            {#each routes as route (route.id)}
              <option value={route.id}>
                {route.name} ({priorityLabel(route.priority as SegmentPriority)})
              </option>
            {/each}
          {/if}
        </select>
      </label>
      {#if routesError}
        <div class="alert error" role="alert">{routesError}</div>
      {/if}
    </div>

    {#if selectedRouteId}
      {#if segmentsError}
        <div class="alert error" role="alert">{segmentsError}</div>
      {/if}
      {#if rowError}
        <div class="alert error" role="alert">{rowError}</div>
      {/if}

      {#if !segmentsLoading && segments.length === 0 && !segmentsError}
        <div class="empty">
          <p class="kicker">Inga härd-segment på den här rundan än.</p>
          <p>Klicka på <strong>Skapa segment</strong> för att markera den första delsträckan.</p>
        </div>
      {:else if segments.length > 0}
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Namn</th>
                <th scope="col">Prioritet</th>
                <th scope="col">Status</th>
                <th scope="col">Aktiv från</th>
                <th scope="col">Aktiv till</th>
                <th scope="col">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {#each segments as record (record.id)}
                {@const active = isActive(record)}
                <tr class:inactive={!active}>
                  <td><strong>{record.name}</strong></td>
                  <td>{priorityLabel(record.priority)}</td>
                  <td>
                    {#if active}
                      <span class="badge active">Aktivt</span>
                    {:else}
                      <span class="badge dim">Inaktivt</span>
                    {/if}
                  </td>
                  <td class="dim">{record.activeFrom.split('T')[0]}</td>
                  <td class="dim">{record.activeTo ? record.activeTo.split('T')[0] : '—'}</td>
                  <td class="actions">
                    <button
                      type="button"
                      class="btn small secondary"
                      onclick={() => startEdit(record)}
                      disabled={pendingActionId === record.id}
                    >
                      Redigera
                    </button>
                    {#if active}
                      <button
                        type="button"
                        class="btn small danger"
                        onclick={() => handleDeactivate(record)}
                        disabled={pendingActionId === record.id}
                      >
                        Inaktivera
                      </button>
                    {:else}
                      <button
                        type="button"
                        class="btn small secondary"
                        onclick={() => handleReactivate(record)}
                        disabled={pendingActionId === record.id}
                      >
                        Aktivera
                      </button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  {/if}

  {#if mode === 'create' || mode === 'edit'}
    <div class="editor">
      <div class="editor-header">
        <p class="eyebrow accent">{mode === 'create' ? 'Nytt segment' : 'Redigera segment'}</p>
        <h3>
          {mode === 'create' ? 'Markera en delsträcka' : (selectedSegment?.name ?? 'Segment')}
        </h3>
        <p class="dim">
          Geometri är MULTILINESTRING i WGS84 (SRID 4326). Format:
          <code>MULTILINESTRING((lon1 lat1, lon2 lat2, ...))</code>. Kart-rit-flöde kommer i en
          senare iteration; just nu klipp/klistra koordinater från externt verktyg eller redigera
          mallen nedan.
        </p>
      </div>

      <form
        class="form"
        onsubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <label class="field">
          <span>Namn</span>
          <input type="text" maxlength="100" bind:value={formName} required disabled={isSaving} />
        </label>

        <label class="field">
          <span>Prioritet</span>
          <select bind:value={formPriority} disabled={isSaving}>
            {#each PRIORITIES as p (p)}
              <option value={p}>{priorityLabel(p)}</option>
            {/each}
          </select>
        </label>

        <label class="field">
          <span>Geometri (WKT MULTILINESTRING)</span>
          <textarea
            rows="4"
            bind:value={formGeometry}
            disabled={isSaving}
            spellcheck="false"
            class="wkt"
          ></textarea>
        </label>

        <div class="field-row">
          <label class="field">
            <span>Aktiv från (valfri)</span>
            <input type="datetime-local" bind:value={formActiveFrom} disabled={isSaving} />
          </label>
          <label class="field">
            <span>Aktiv till (valfri)</span>
            <input type="datetime-local" bind:value={formActiveTo} disabled={isSaving} />
          </label>
        </div>

        {#if formError}
          <div class="alert error" role="alert">{formError}</div>
        {/if}

        <div class="form-actions">
          <button type="submit" class="btn primary" disabled={isSaving}>
            {isSaving ? 'Sparar…' : mode === 'create' ? 'Skapa segment' : 'Spara ändringar'}
          </button>
          <button type="button" class="btn secondary" onclick={cancelEditor} disabled={isSaving}>
            Avbryt
          </button>
        </div>
      </form>
    </div>
  {/if}
</section>

<style>
  .segments {
    display: grid;
    gap: 1rem;
  }
  header {
    display: grid;
    gap: 0.4rem;
  }
  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  .eyebrow.accent {
    color: var(--glow);
  }
  h2,
  h3 {
    margin: 0;
    color: var(--text);
  }
  h2 {
    font-size: 1.4rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  .lead {
    margin: 0;
    color: var(--text-dim);
    line-height: 1.55;
    max-width: 60ch;
  }
  .dim {
    color: var(--text-dim);
  }
  .route-selector {
    display: grid;
    gap: 0.4rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--rule);
  }
  .table-wrap {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    padding: 0.55rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid var(--rule);
    vertical-align: top;
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-dim);
    font-weight: 600;
  }
  tr.inactive {
    opacity: 0.6;
  }
  .badge {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--rule);
    color: var(--text-dim);
  }
  .badge.active {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--glow);
  }
  .badge.dim {
    color: var(--text-dim);
  }
  .actions {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text);
    padding: 0.45rem 0.95rem;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.85rem;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--bg);
    font-weight: 600;
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary:hover:not(:disabled) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .btn.small {
    padding: 0.3rem 0.65rem;
    font-size: 0.8rem;
  }
  .btn.danger {
    background: var(--danger-soft);
    border-color: var(--danger);
    color: var(--text);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  /* icon-btn-styles flyttade till RitaView (UX-fix 2026-05-05) — `+` och
     Refresh äger nu Rita-layer-toolbar-raden, inte enskilda sub-vyer. */
  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--rule);
    border-radius: 6px;
    text-align: center;
    color: var(--text-dim);
  }
  .kicker {
    font-weight: 600;
    color: var(--text);
    margin: 0 0 0.25rem;
  }
  .editor {
    display: grid;
    gap: 1rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.5rem;
    max-width: 720px;
  }
  .editor-header {
    display: grid;
    gap: 0.3rem;
  }
  .editor-header code {
    font-family:
      ui-monospace,
      SFMono-Regular,
      Consolas,
      Liberation Mono,
      monospace;
    font-size: 0.85em;
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 3px;
    padding: 0 0.3rem;
  }
  .form {
    display: grid;
    gap: 0.85rem;
  }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .field input,
  .field textarea,
  .field select {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    padding: 0.45rem 0.6rem;
  }
  .field textarea.wkt {
    font-family:
      ui-monospace,
      SFMono-Regular,
      Consolas,
      Liberation Mono,
      monospace;
    font-size: 0.85rem;
    resize: vertical;
  }
  .field input:focus,
  .field textarea:focus,
  .field select:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
  @media (max-width: 640px) {
    .field-row {
      grid-template-columns: 1fr;
    }
  }
</style>
